import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { useEffect, useRef, useState } from "react"

import { MessageText } from "@/components/Chat/MessageStyles.ts"
import { StreamedTextBlock } from "@/components/Chat/StreamedTextBlock.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { ReasoningResult_Diff } from "@/components/ReasoningStep/ReasoningResult_Diff.tsx"
import { ReasoningResult_Message } from "@/components/ReasoningStep/ReasoningResult_Message.tsx"
import { ReasoningResult_ScriptDiff } from "@/components/ReasoningStep/ReasoningResult_ScriptDiff.tsx"
import { ReasoningResult_SshCommand } from "@/components/ReasoningStep/ReasoningResult_SshCommand.tsx"
import { ReasoningResult_TodoList } from "@/components/ReasoningStep/ReasoningResult_TodoList.tsx"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { ChevronRightLight, Icon12 } from "@/icons/Icons.ts"
import { preventTextSelection } from "@/lib/std.ts"
import { Rotate0, Rotate90 } from "@/styles/mixins.ts"

import type {
  AgentStep,
  AiChatMessageAgentIterationStep,
  UUIToolCallObservation
} from "@/lib/client/generated/TypeDefinitions.ts"

type ReasoningStepProps = {
  step: AgentStep | AiChatMessageAgentIterationStep,
  taskId: string,
  isRunning: boolean
}

export const ReasoningStep = (props: ReasoningStepProps) => {
  const reasoning = props.step.reasoning
  const result = props.step.result
  const substeps = "substeps" in props.step ? props.step.substeps : null

  const hasSubsteps = substeps && substeps.length > 0
  const isInProgress = props.step.inProgress && props.isRunning

  const [expanded, setExpanded] = useState(false)
  const wasInProgressRef = useRef<boolean | null>(null)

  // Auto-expand when step becomes in progress, auto-collapse when completed
  useEffect(() => {
    const wasInProgress = wasInProgressRef.current
    wasInProgressRef.current = isInProgress

    if (wasInProgress !== null) {
      if (isInProgress && !wasInProgress) {
        setExpanded(true)
      } else if (!isInProgress && wasInProgress) {
        setExpanded(false)
      }
    }
  }, [isInProgress])

  if (!result && !reasoning) {
    return null
  }

  const handleToggle = () => {
    if (hasSubsteps) {
      setExpanded(!expanded)
    }
  }

  return (
    <Vertical>
      {reasoning && (
        <Reasoning>
          <StreamedTextBlock
            text={reasoning}
            isNew
            isStreamCompleted
            isStreamed={false}
          />
        </Reasoning>
      )}
      {result && (
        <ResultWithChevron
          onClick={hasSubsteps ? handleToggle : undefined}
          onMouseDown={hasSubsteps ? preventTextSelection : undefined}
          className={hasSubsteps ? Clickable : undefined}
        >
          <ReasoningResultChooser
            result={result}
            taskId={props.taskId}
            inProgress={props.step.inProgress && props.isRunning}
          />
          {hasSubsteps && (
            <ChevronContainer>
              <ChevronRightLight
                className={cx(expanded ? Rotate90 : Rotate0, Icon12)}
                color="var(--secondary-text-color)"
              />
            </ChevronContainer>
          )}
        </ResultWithChevron>
      )}

      {hasSubsteps && expanded && (
        <SubstepsScrollable>
          <SubstepsContainer>
            {substeps.map((substep, index) => (
              <Substep
                key={`substep-${substep.id ?? index}`}
                step={substep}
                taskId={props.taskId}
                isRunning={props.isRunning}
              />
            ))}
          </SubstepsContainer>
        </SubstepsScrollable>
      )}
    </Vertical>
  )
}

const Substep = (props: {
  step: AgentStep,
  taskId: string,
  isRunning: boolean
}) => {
  const reasoning = props.step.reasoning
  const result = props.step.result

  if (!result && !reasoning) {
    return null
  }

  return (
    <SubstepContent>
      {reasoning && (
        <Reasoning>
          <StreamedTextBlock
            text={reasoning}
            isNew
            isStreamCompleted
            isStreamed={false}
          />
        </Reasoning>
      )}
      {result && (
        <ReasoningSecondLevelText>
          <ReasoningResultChooser
            result={result}
            taskId={props.taskId}
            inProgress={props.step.inProgress && props.isRunning}
          />
        </ReasoningSecondLevelText>
      )}
    </SubstepContent>
  )
}

const ReasoningResultChooser = (props: {
  result: UUIToolCallObservation,
  taskId: string,
  inProgress: boolean
}) => {
  if (props.result.className === "Message") {
    return <ReasoningResult_Message content={props.result} inProgress={props.inProgress} />
  }

  if (props.result.className == "ScriptDiff") {
    return <ReasoningResult_ScriptDiff content={props.result} />
  }

  if (props.result.className === "Diff") {
    return <ReasoningResult_Diff content={props.result} />
  }

  if (props.result.className === "SshCommand") {
    return <ReasoningResult_SshCommand content={props.result} inProgress={props.inProgress} taskId={props.taskId} />
  }

  if (props.result.className === "TodoList") {
    return <ReasoningResult_TodoList content={props.result} />
  }
}

const ResultWithChevron = styled(Horizontal)`
  align-items: center;
  margin-top: 4px;
  gap: 4px;
`

const ChevronContainer = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
`

const Clickable = css`
  cursor: pointer;
`

const Reasoning = styled(MessageText)`
  margin-top: 16px;
`

const ReasoningSecondLevelText = styled.div`
  margin-top: 4px;
`

const SubstepsScrollable = styled(Scrollable)`
  max-height: 160px;
  margin-left: 16px;
  margin-top: 4px;
  margin-bottom: 8px;
`

const SubstepsContainer = styled(Vertical)`
  :first-child ${Reasoning} {
    margin-top: 8px;
  }
`

const SubstepContent = styled.div``
